import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type DocumentNode, NetworkStatus, type OperationVariables, useQuery } from "@apollo/client";

import { RETRY_TRANSIENT } from "../../apollo/provider/createTransientRetryLink";

interface Page<TData> {
    cursor: (data: TData) => string | null | undefined;
    merge: (previous: TData, next: TData) => TData;
}

export interface CursorPagedQuery<TData> {
    response: TData | undefined;
    error: Error | undefined;
    hasMore: boolean;
    fetchMore: () => void;
    loadingMore: boolean;
    hasMoreError: boolean;
    // Pass the lookup that failed (for example the selected Lens) to retry it instead of the page query.
    refetch: (query?: () => Promise<unknown>) => void;
    loading: boolean;
    hasError: boolean;
    hasRefreshError: boolean;
}

/**
 * Cursor pagination over a `cache-and-network` query. Each distinct variables/skip pair is a scope;
 * refreshes bump the scope generation so pages requested before a refresh, selection change or
 * unmount never write into the cache that replaced them.
 */
export const useCursorPagedQuery = <TData, TVariables extends { input: { cursor?: string | null } }>(
    document: DocumentNode,
    variables: TVariables,
    skip: boolean,
    page: Page<TData>,
): CursorPagedQuery<TData> => {
    const scope = useMemo(() => ({ variables, skip, generation: 0, refreshing: false }), [variables, skip]);
    const currentScope = useRef(scope);
    currentScope.current = scope;
    const fetchingMore = useRef<{ scope: typeof scope; generation: number } | null>(null);
    const [pageError, setPageError] = useState<typeof fetchingMore.current>(null);
    useEffect(
        () => () => {
            scope.generation += 1;
        },
        [scope],
    );
    const {
        data,
        loading,
        error,
        networkStatus,
        fetchMore: apolloFetchMore,
        refetch: apolloRefetch,
    } = useQuery<TData, TVariables & OperationVariables>(document, {
        variables,
        skip,
        // Recent months are still estimated and newly paid records must appear without hiding cached data.
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        context: RETRY_TRANSIENT,
    });
    const response = skip ? undefined : data;
    const cursor = response ? page.cursor(response) : undefined;
    const refetch = useCallback(
        (query?: () => Promise<unknown>) => {
            // A skipped page query has nothing to refetch unless the caller supplies the blocked lookup.
            if (skip && !query) return;
            scope.generation += 1;
            scope.refreshing = true;
            setPageError(null);
            void (query ?? apolloRefetch)()
                .catch(() => {
                    // Query state provides the scoped retry feedback.
                })
                .finally(() => {
                    scope.refreshing = false;
                });
        },
        [apolloRefetch, scope, skip],
    );
    const fetchMore = useCallback(() => {
        if (
            skip ||
            loading ||
            scope.refreshing ||
            !cursor ||
            (fetchingMore.current?.scope === scope && fetchingMore.current.generation === scope.generation)
        )
            return;
        const request = { scope, generation: scope.generation };
        fetchingMore.current = request;
        setPageError(null);
        void apolloFetchMore({
            variables: { ...variables, input: { ...variables.input, cursor } },
            updateQuery: (previous, { fetchMoreResult }) => {
                if (currentScope.current !== scope || scope.generation !== request.generation) return previous;
                if (page.cursor(fetchMoreResult) === cursor) throw new Error("Pagination did not advance");
                return page.merge(previous, fetchMoreResult);
            },
        })
            .catch(() => {
                if (currentScope.current === scope && scope.generation === request.generation) setPageError(request);
            })
            .finally(() => {
                if (fetchingMore.current === request) fetchingMore.current = null;
            });
    }, [apolloFetchMore, cursor, loading, page, scope, skip, variables]);
    return {
        response,
        error,
        hasMore: Boolean(cursor),
        fetchMore,
        loadingMore: networkStatus === NetworkStatus.fetchMore,
        hasMoreError: pageError?.scope === scope && pageError.generation === scope.generation,
        refetch,
        loading: loading && !response,
        hasError: Boolean(error && !response),
        hasRefreshError: !skip && Boolean(error && response),
    };
};
